import React, { FC, useCallback, useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useFilterBarParams } from 'src/common/components/filter-bar/use-params';
import { DataTable } from 'src/common/components/table/data-table';
import { generateColumn } from 'src/common/components/table/LegacyTable';
import { useCurrentOrganization } from 'src/store/organization';
import { apiGetContentInsights } from 'src/utils/journeyApi';
import { useDepsMatchingCallbackRef } from 'src/utils/react/deps-matching-callback-ref.hook';
import { FixedLoadingSpinner } from 'src/common/components/loading-spinner';
import { Pagination } from 'src/common/Pagination';

import { ContentInsight } from '../content-insight.interface';

import { getTotalViews, getAverageViewTime, getUsedInJourneys, getClickThroughRate } from './metrics';
import {
  TableSortableColumnHeader,
  TableSortableColumnHeaderOrderType,
  TableSortableColumnHeaderSortType,
} from 'src/common/components/table/sortable-column-header';
import { TableHeaderWithTooltip } from '../table-header-with-tooltip';
import { formatDateTime } from 'src/utils/date';
import { Helmet } from 'react-helmet';
import { formatNumber } from 'src/utils/text';
import { Nullable } from 'src/types/nullable.type';
import isNull from 'lodash/isNull';
import { LibraryHoverableCardSmall } from 'src/common/components/library/hoverable-content-card-small';

type DashboardInsightsContentProps = {};

type DashboardInsightsContentListHeaderTypes = 'total_views' | 'session_uuid' | 'average_time' | 'name' | 'updated_at' | 'created_at';

type DashboardInsightsContentListHeaderSortTypes =
  TableSortableColumnHeaderSortType<DashboardInsightsContentListHeaderTypes>;

export const DashboardInsightsContentAll: FC<DashboardInsightsContentProps> = () => {
  const history = useHistory();
  const [contentsInsights, setContentsInsights] = useState<any[]>([]);
  const [loading, setLoading] = useState<Nullable<boolean>>(null);
  const [pages, setPages] = useState(1);

  const currentOrganization = useCurrentOrganization((state) => state.currentOrganization);

  const {
    filterBarParams: { search_term, page, sort_by, sort_order },
    updatePage,
    updateSort,
  } = useFilterBarParams({
    sort_by: 'total_views',
  });

  const onFetchContentsInsightsRef = useDepsMatchingCallbackRef(
    (response: any) => {
      setContentsInsights(response.contents);
      setPages(response.pages);
      setLoading(false);
    },
    [currentOrganization.id, search_term, page]
  );

  const fetchContentInsights = useCallback(async () => {
    const params = {
      page: page,
      ...(search_term && { search_str: search_term }),
      ...(sort_by && { sort_by: sort_by }),
      ...(sort_order && { sort_order: sort_order }),
    };

    try {
      setLoading(true);
      const response = await apiGetContentInsights(currentOrganization.id, params);
      onFetchContentsInsightsRef.current &&
        onFetchContentsInsightsRef.current(response, [currentOrganization.id, search_term, page]);
    } catch (e) {
      console.error(e);
    }
  }, [onFetchContentsInsightsRef, currentOrganization.id, search_term, page, sort_by, sort_order]);

  useEffect(() => {
    fetchContentInsights();
  }, [fetchContentInsights]);

  const onTableHeaderClick = useCallback(
    (sortStack: DashboardInsightsContentListHeaderSortTypes) => {
      updateSort(sortStack.sortName, sortStack.orderBy);
    },
    [updateSort]
  );

  const columns = useMemo(
    () => [
      generateColumn({
        Header: (
          <TableSortableColumnHeader
            isActive={sort_by === 'name'}
            activeOrderType={sort_order as TableSortableColumnHeaderOrderType}
            accessor='name'
            onClick={onTableHeaderClick}
            title='Content'
            tooltip='Name of content'
          />
        ),
        accessor: 'name',
        width: 462,
        allowShrinking: true,
        Cell: ({ row, index }: { row: any; index: number }) => {
          return <LibraryHoverableCardSmall clickable content={row.original.content} />;
        },
      }),
      generateColumn({
        Header: (
          <TableHeaderWithTooltip tooltip='Total times this content is used across Journeys'>
            Used in Journeys
          </TableHeaderWithTooltip>
        ),
        accessor: 'journeys',
        width: 132,
        allowShrinking: true,
        Cell: ({ row, index }: { row: any; index: number }) => {
          return formatNumber(getUsedInJourneys(row.original));
        },
      }),
      generateColumn({
        Header: (
          <TableSortableColumnHeader
            isActive={sort_by === 'total_views'}
            activeOrderType={sort_order as TableSortableColumnHeaderOrderType}
            accessor='total_views'
            onClick={onTableHeaderClick}
            title='Total views'
            tooltip='Total times this content was viewed across Journeys'
          />
        ),
        accessor: 'total_views',
        width: 132,
        allowShrinking: true,
        Cell: ({ row, index }: { row: any; index: number }) => {
          return formatNumber(getTotalViews(row.original));
        },
      }),
      generateColumn({
        Header: (
          <TableSortableColumnHeader
            isActive={sort_by === 'average_time'}
            activeOrderType={sort_order as TableSortableColumnHeaderOrderType}
            accessor='average_time'
            onClick={onTableHeaderClick}
            title='Average time'
            tooltip='Average time spent viewing this content across Journeys'
          />
        ),
        accessor: 'average_time',
        width: 132,
        allowShrinking: true,
        Cell: ({ row, index }: { row: any; index: number }) => {
          return getAverageViewTime(row.original);
        },
      }),
      generateColumn({
        Header: (
          <TableHeaderWithTooltip tooltip='How many users view this step across Journeys'>
            Click-through rate
          </TableHeaderWithTooltip>
        ),
        accessor: 'ctr',
        width: 190,
        allowShrinking: true,
        Cell: ({ row, index }: { row: any; index: number }) => {
          return getClickThroughRate(row.original);
        },
      }),
      generateColumn({
        Header: (
          <TableSortableColumnHeader
            isActive={sort_by === 'updated_at'}
            activeOrderType={sort_order as TableSortableColumnHeaderOrderType}
            accessor='updated_at'
            onClick={onTableHeaderClick}
            title='Updated'
            tooltip='When this content was updated'
          />
        ),
        accessor: 'updated_at',
        width: 180,
        allowShrinking: false,
        Cell: ({ row, index }: { row: any; index: number }) => {
          return row.original.content.updated_at ? formatDateTime(row.original.content.updated_at) : '';
        },
      }),
    ],
    [sort_by, sort_order, onTableHeaderClick]
  );

  const onPageChange = useCallback((e: any, newPage: number) => {
    updatePage(newPage);
  }, []);

  const onRowClick = useCallback(
    (row: any) => {
      const contentInsight = row.original as ContentInsight;
      history.push(`/${currentOrganization.slug}/insights/content/${contentInsight.content.uuid}`);
    },
    [currentOrganization.slug, history]
  );

  const renderContent = () => {
    if (loading || isNull(loading)) {
      return <FixedLoadingSpinner />;
    }

    if (contentsInsights.length > 0) {
      return <DataTable columns={columns} data={contentsInsights} onRowClick={onRowClick} />;
    }

    if (search_term) {
      return <div className='text-bedrock-p text-bedrock-black'>No content found</div>;
    }

    return (
      <div className='max-w-[640px] text-bedrock-p text-bedrock-black'>
        Once you create some content, you will see their insights here.
      </div>
    );
  };

  return (
    <div className='flex flex-col'>
      <Helmet>
        <title>Journey | Insights | Content</title>
      </Helmet>
      {renderContent()}
      {pages > 1 && (
        <div className='mt-6'>
          <Pagination count={pages} page={page} onChange={onPageChange} />
        </div>
      )}
    </div>
  );
};
